import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useWatch } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import EventAttendeeDialog, {
  UPDATE_EVENT_ATTENDEE,
} from '@web/src/features/event/event-attendee-dialog/event-attendee-dialog'
import EventAttendeesHeader from '@web/src/features/event/event-attendees-header/event-attendees-header'
import { getEventAttendanceStatusBadge, getEventRoleBadge } from '@web/src/features/event/utils/eventBadges'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { capitalizeFirstLetter } from '@web/src/lib/formatters'
import { ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  CheckIcon,
  CircleDollarSignIcon,
  ClipboardListIcon,
  EllipsisIcon,
  PencilIcon,
  ShieldQuestionIcon,
  ShieldUserIcon,
  UserIcon,
  UserMinusIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  EventAttendanceFilterStatus,
  EventAttendeeSortEnum,
  EventAttendeesListPageQuery,
  EventAttendeesListPageQueryVariables,
  EventAttendeesPageQuery,
  EventAttendeesPageQueryVariables,
  EventRole,
  RemoveEventAttendeeMutation,
  RemoveEventAttendeeMutationVariables,
} from 'types/graphql'
import { z } from 'zod'

dayjs.extend(relativeTime)

export const EVENT_QUERY: TypedDocumentNode<EventAttendeesPageQuery, EventAttendeesPageQueryVariables> = gql`
  query EventAttendeesPageQuery($eventIdOrSlug: String!) {
    event: event(eventIdOrSlug: $eventIdOrSlug) {
      id
      slug
      ownerId
      name
      attendeeApproval
      canEdit
      canInvite
      admission
      capacity
      admissionCouples
      admissionFemale
      admissionMale
      admissionNote
      waitlistEnabled
      attendanceCounts {
        goingCount
        maybeCount
        notGoingCount
        noneCount
        totalCount
        waitlistCount
      }
      joinRequestCounts {
        totalCount
        pendingCount
        approvedCount
        rejectedCount
      }
    }
  }
`

export const EVENT_ATTENDEES_QUERY: TypedDocumentNode<
  EventAttendeesListPageQuery,
  EventAttendeesListPageQueryVariables
> = gql`
  query EventAttendeesListPageQuery($page: Int, $filter: EventAttendeeFilter, $sortBy: EventAttendeeSortEnum) {
    eventAttendeeListPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        createdAt
        eventId
        profileId
        role
        invitedById
        status
        peopleQty
        paymentAmount
        paymentStatus
        notes
        profile {
          ...ProfileBasicInfo
        }
        invitedBy {
          ...ProfileBasicInfo
        }
        waitlistPriority
      }
      count
      perPage
    }
  }
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

export const REMOVE_EVENT_ATTENDEE: TypedDocumentNode<
  RemoveEventAttendeeMutation,
  RemoveEventAttendeeMutationVariables
> = gql`
  mutation RemoveEventAttendeeMutation($id: String!, $profileId: String!) {
    deleteEventAttendee(id: $id, profileId: $profileId) {
      id
    }
  }
`

type AttendeesSearchInput = {
  page?: number
  name?: string
  role?: string
  status?: string
  sortBy?: string
}

type AttendeesLoaderDeps = {
  page: number
  name?: string
  role?: string
  status: string
  sortBy: string
}

function resolveAttendeesSearchParams(search: AttendeesSearchInput): AttendeesLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    role: search.role,
    status: search.status ?? 'RESPONSES',
    sortBy: search.sortBy ?? 'STATUS',
  }
}

function attendeesStatusFilter(status: string): EventAttendanceFilterStatus[] | undefined {
  if (status === 'ALL') {
    return undefined
  }
  if (status === 'RESPONSES') {
    return ['GOING', 'MAYBE', 'NOTGOING']
  }
  return [status as EventAttendanceFilterStatus]
}

function eventAttendeesQueryVariables(
  eventIdOrSlug: string,
  deps: AttendeesLoaderDeps
): EventAttendeesListPageQueryVariables {
  return {
    page: deps.page,
    filter: {
      eventId: eventIdOrSlug,
      name: deps.name,
      role: deps.role as EventRole | undefined,
      statuses: attendeesStatusFilter(deps.status),
    },
    sortBy: deps.sortBy as EventAttendeeSortEnum,
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/event/$eventIdOrSlug_/attendees')({
  component: EventAttendeesPage,
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    role: z.string().optional(),
    status: z.string().optional(),
    sortBy: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        role: '',
        status: 'RESPONSES',
        sortBy: 'STATUS',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Attendees List | ${env.VITE_APP_NAME}`,
      description: 'Browse a list of event attendees.',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loaderDeps: ({ search }) => resolveAttendeesSearchParams(search),
  loader: async ({ context, deps, params }) => {
    const resolvedDeps = deps as AttendeesLoaderDeps
    const variables = eventAttendeesQueryVariables(params.eventIdOrSlug, resolvedDeps)

    const [{ queryRef: eventQueryRef }, { queryRef: attendeesQueryRef }] = await Promise.all([
      queryAndPreload(context, EVENT_QUERY, {
        variables: { eventIdOrSlug: params.eventIdOrSlug },
      }),
      queryAndPreload(context, EVENT_ATTENDEES_QUERY, { variables }),
    ])

    return {
      eventQueryRef,
      attendeesQueryRef,
    }
  },
})

function EventAttendeesPage() {
  const { t } = useTranslation()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveAttendeesSearchParams(search), [search])
  const navigate = useNavigate()
  const formRef = useRef(null)
  const [removeModal, setRemoveModal] = useState(false)
  const [attendeeToRemove, setAttendeeToRemove] = useState(null)
  const [selectedAttendee, setSelectedAttendee] = useState(null)
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const { cache } = useApolloClient()

  const { eventQueryRef, attendeesQueryRef } = Route.useLoaderData()
  const { data: eventData, error: eventError } = useReadQuery(eventQueryRef as QueryRef<EventAttendeesPageQuery>)
  const { data, error } = useReadQuery(attendeesQueryRef as QueryRef<EventAttendeesListPageQuery>)
  const [isLoading, setIsLoading] = useState(false)

  const routerIsPending = useRouterState({ select: (s) => s.status === 'pending' })

  // Prevent hydration mismatch by only setting loading state on client
  useEffect(() => {
    setIsLoading(routerIsPending)
  }, [routerIsPending])

  const formDefaultValues = useMemo(
    () => ({
      page: resolvedSearch.page,
      name: resolvedSearch.name ?? '',
      role: resolvedSearch.role as EventRole | undefined,
      status: resolvedSearch.status,
      sortBy: resolvedSearch.sortBy,
    }),
    [resolvedSearch.page, resolvedSearch.name, resolvedSearch.role, resolvedSearch.status, resolvedSearch.sortBy]
  )

  const formMethods = useForm({
    defaultValues: formDefaultValues,
  })

  const [updateEventAttendee] = useMutation(UPDATE_EVENT_ATTENDEE, {
    onCompleted: () => {
      toast.success(t('Attendee updated successfully'))
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  const [removeEventAttendee] = useMutation(REMOVE_EVENT_ATTENDEE, {
    onCompleted: (mutationData) => {
      toast.success(t('Attendee removed successfully'))
      // Remove the attendee from the cache
      cache.evict({ id: cache.identify(mutationData.deleteEventAttendee) })
      setRemoveModal(false)
    },
    onError: (err) => {
      toast.error(err.message)
      setRemoveModal(false)
    },
  })

  const filterResults = useCallback(() => {
    const curPage = formMethods.getValues('page')
    const name = formMethods.getValues('name')
    const role = formMethods.getValues('role') as EventRole
    const status = formMethods.getValues('status')
    const sortBy = formMethods.getValues('sortBy') as EventAttendeeSortEnum

    // Navigate with new search params - this will trigger the loader to re-run
    navigate({
      search: {
        page: curPage,
        name: name || undefined,
        sortBy: sortBy,
        role: role || undefined,
        status: status || undefined,
      },
      replace: true,
      viewTransition: false,
    } as never)
  }, [formMethods, navigate])

  const handleLiveFilter = useCallback(() => {
    filterResults()
  }, [filterResults])

  const handleResetFilters = () => {
    navigate({
      search: {
        page: 1,
        status: 'RESPONSES',
        sortBy: 'STATUS',
      },
      replace: true,
      viewTransition: false,
    } as never)
  }

  const handlePageChange = useCallback(
    (nextPage) => {
      navigate({
        search: {
          ...search,
          page: nextPage.page,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({
        search: {
          ...search,
          page: 1,
          name: value || undefined,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleAction = (
    attendee: NonNullable<EventAttendeesListPageQuery['eventAttendeeListPage']>['entries'][number],
    action: string
  ) => {
    if (action === 'remove') {
      setAttendeeToRemove(attendee)
      setRemoveModal(true)
    } else if (action === 'EDIT') {
      setSelectedAttendee(attendee)
      setPaymentDialogOpen(true)
    } else if (action === 'set-host-role') {
      updateEventAttendee({
        variables: {
          id: attendee.id,
          input: { role: 'HOST' },
        },
      })
    } else if (action === 'set-attendee-role') {
      updateEventAttendee({
        variables: {
          id: attendee.id,
          input: { role: 'ATTENDEE' },
        },
      })
    } else if (action === 'going') {
      updateEventAttendee({
        variables: {
          id: attendee.id,
          input: {
            status: 'GOING',
          },
        },
      })
    } else if (action === 'maybe') {
      updateEventAttendee({
        variables: {
          id: attendee.id,
          input: {
            status: 'MAYBE',
          },
        },
      })
    } else if (action === 'notgoing') {
      updateEventAttendee({
        variables: {
          id: attendee.id,
          input: { status: 'NOTGOING' },
        },
      })
    } else if (action === 'waitlisted') {
      updateEventAttendee({
        variables: {
          id: attendee.id,
          input: { status: 'WAITLISTED' },
        },
      })
    }
  }

  const pageData = useMemo(
    () => data?.eventAttendeeListPage || { entries: [], count: 0, perPage: 24 },
    [data?.eventAttendeeListPage]
  )
  const currentPage = useWatch({ control: formMethods.control, name: 'page' })
  const watchName = useWatch({ control: formMethods.control, name: 'name' })

  if (error || eventError) {
    return <ErrorAlert error={error || eventError} />
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-2">
      <div className="flex w-full flex-col items-stretch gap-4">
        <EventAttendeesHeader activeTab={'attendees'} event={eventData?.event} />

        <Form className="w-full" formMethods={formMethods} ref={formRef}>
          <div className="flex w-full flex-col items-stretch gap-4">
            {/* Search and Filters */}
            <SearchExpand
              clearSearchLabel={t('Clear search')}
              inputProps={{ 'aria-label': t('Filter attendees') }}
              loading={isLoading}
              minChars={0}
              name="name"
              onSearchChange={handleSearchChange}
              placeholder={t('Filter attendees by name...')}
              toggleFiltersLabel={t('Toggle filters')}
            >
              <Card className="gap-0 py-0">
                <CardContent className="px-2 py-3">
                  <div className="flex w-full flex-col gap-4">
                    <div className="flex w-full flex-wrap items-start gap-2">
                      <NativeSelectFormField label={t('Roles')} name="role" onValueChange={handleLiveFilter}>
                        <option value="">{t('All Roles')}</option>
                        <option value="OWNER">{t('Owners')}</option>
                        <option value="HOST">{t('Hosts')}</option>
                        <option value="ATTENDEE">{t('Attendees')}</option>
                      </NativeSelectFormField>

                      {eventData?.event?.canEdit && (
                        <NativeSelectFormField label={t('Status')} name="status" onValueChange={handleLiveFilter}>
                          <option value="RESPONSES">{t('All Responses')}</option>
                          <option value="ALL">{t('All Status')}</option>
                          <option value="GOING">
                            {t('Going ({{count}})', { count: eventData?.event?.attendanceCounts?.goingCount || 0 })}
                          </option>
                          <option value="MAYBE">
                            {t('Maybe ({{count}})', { count: eventData?.event?.attendanceCounts?.maybeCount || 0 })}
                          </option>
                          <option value="NOTGOING">
                            {t('Not Going ({{count}})', {
                              count: eventData?.event?.attendanceCounts?.notGoingCount || 0,
                            })}
                          </option>
                          {eventData?.event?.waitlistEnabled && (
                            <option value="WAITLISTED">
                              {t('Waitlisted ({{count}})', {
                                count: eventData?.event?.attendanceCounts?.waitlistCount || 0,
                              })}
                            </option>
                          )}
                          {eventData?.event?.canEdit && (
                            <option value="NONE">
                              {t('No Response ({{count}})', {
                                count: eventData?.event?.attendanceCounts?.noneCount || 0,
                              })}
                            </option>
                          )}
                        </NativeSelectFormField>
                      )}

                      <NativeSelectFormField label={t('Sort By')} name="sortBy" onValueChange={handleLiveFilter}>
                        <option value="NAME">{t('Name')}</option>
                        <option value="UPDATED">{t('Last Updated')}</option>
                        {eventData?.event?.canEdit && <option value="PAYMENT_STATUS">{t('Payment Status')}</option>}
                        {eventData?.event?.canEdit && <option value="ROLE">{t('Role')}</option>}
                        <option value="STATUS">{t('Status')}</option>
                        {eventData?.event?.waitlistEnabled && eventData?.event?.canEdit && (
                          <option value="WAITLIST_PRIORITY">{t('Waitlist Priority')}</option>
                        )}
                      </NativeSelectFormField>
                    </div>

                    <div className="flex w-full justify-end">
                      <Button onClick={handleResetFilters} type="button" variant="secondary">
                        {t('Reset')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SearchExpand>

            <div className="flex w-full flex-wrap items-center justify-center gap-2">
              <Badge variant="outline">
                {t('{{count}} Going', { count: eventData?.event?.attendanceCounts?.goingCount })}
              </Badge>
              <Badge variant="outline">
                {t('{{count}} Maybe', { count: eventData?.event?.attendanceCounts?.maybeCount })}
              </Badge>
              <Badge variant="outline">
                {t('{{count}} Not Going', { count: eventData?.event?.attendanceCounts?.notGoingCount })}
              </Badge>
              {eventData?.event?.waitlistEnabled && (
                <Badge variant="outline">
                  {t('{{count}} Waitlist', { count: eventData?.event?.attendanceCounts?.waitlistCount })}
                </Badge>
              )}
              {eventData?.event?.canEdit && (
                <Badge variant="outline">
                  {t('{{count}} Invited', { count: eventData?.event?.attendanceCounts?.noneCount })}
                </Badge>
              )}
            </div>

            {/* Results */}
            {pageData.entries.length > 0 && (
              <div className="flex w-full flex-col gap-4">
                <div className="flex w-full flex-col gap-1">
                  {pageData.entries.map((attendee) => {
                    return (
                      <Card
                        className={cn(
                          'w-full cursor-pointer overflow-hidden border-border bg-muted/50 py-0 transition-colors hover:border-ring hover:bg-muted data-[size=sm]:py-1',
                          attendee.status === 'NOTGOING' && 'opacity-40'
                        )}
                        key={attendee.id}
                        size="sm"
                      >
                        <CardContent className="px-3 py-1">
                          <div className="flex w-full flex-col gap-0">
                            <div className="flex w-full items-center justify-between gap-2">
                              <div className="flex min-w-0 flex-1 items-center">
                                <SFProfileBanner
                                  className="cursor-pointer"
                                  lowerTitle={
                                    <div className="flex flex-col items-start gap-0">
                                      <div className="flex flex-wrap items-center gap-1">
                                        <Badge size="sm" variant="outline">
                                          {attendee?.peopleQty > 1 ? <UsersIcon /> : <UserIcon />}
                                          <span className="text-sm">{attendee?.peopleQty}</span>
                                        </Badge>

                                        {getEventRoleBadge({ id: attendee.id, role: attendee.role }, 'sm')}
                                        {getEventAttendanceStatusBadge(attendee.status, 'sm')}

                                        {eventData?.event?.canEdit &&
                                          eventData?.event?.admission &&
                                          (attendee.status === 'GOING' || attendee.paymentAmount > 0) && (
                                            <Badge
                                              className={cn(
                                                'font-semibold',
                                                attendee.paymentStatus !== 'UNPAID'
                                                  ? attendee.paymentStatus !== 'PARTIAL'
                                                    ? 'bg-green-600 text-black'
                                                    : 'bg-amber-500 text-black'
                                                  : 'bg-primary text-primary-foreground'
                                              )}
                                              size="sm"
                                            >
                                              <CircleDollarSignIcon />
                                              {t(capitalizeFirstLetter(attendee.paymentStatus.toLocaleLowerCase()))}
                                              {(attendee.paymentStatus === 'PAID' ||
                                                attendee.paymentStatus === 'PARTIAL') &&
                                                ` $${attendee.paymentAmount.toFixed(2)}`}
                                            </Badge>
                                          )}
                                      </div>

                                      {eventData?.event?.canEdit && (
                                        <p className="line-clamp-1 text-muted-foreground text-xs">
                                          {t('Signed up: {{time}}', { time: dayjs(attendee.createdAt).fromNow() })}
                                        </p>
                                      )}

                                      {attendee.invitedBy && eventData?.event?.canEdit && (
                                        <p className="line-clamp-1 text-muted-foreground text-xs">
                                          {t('Invited by: {{name}}', { name: attendee.invitedBy.name })}
                                        </p>
                                      )}
                                    </div>
                                  }
                                  onClick={() =>
                                    navigate({
                                      to: '/profile/$profileId/about',
                                      params: { profileId: attendee.profile.id },
                                    } as never)
                                  }
                                  profile={attendee.profile}
                                  size="xl"
                                />
                              </div>

                              {eventData?.event?.canEdit && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger
                                    render={
                                      <Button
                                        aria-label={t('Attendee actions')}
                                        className="shrink-0 rounded-full"
                                        size="icon"
                                        type="button"
                                      >
                                        <EllipsisIcon />
                                      </Button>
                                    }
                                  />
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleAction(attendee, 'EDIT')}>
                                      <PencilIcon />
                                      {t('Edit')}
                                    </DropdownMenuItem>

                                    <DropdownMenuSeparator />

                                    <DropdownMenuItem
                                      disabled={
                                        attendee.role === 'OWNER' ||
                                        attendee.role === 'HOST' ||
                                        attendee.profileId === eventData?.event?.ownerId
                                      }
                                      onClick={() => handleAction(attendee, 'set-host-role')}
                                    >
                                      <ShieldUserIcon />
                                      {t('Set Role to Host')}
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                      disabled={
                                        attendee.role === 'ATTENDEE' || attendee.profileId === eventData?.event?.ownerId
                                      }
                                      onClick={() => handleAction(attendee, 'set-attendee-role')}
                                    >
                                      <UsersIcon />
                                      {t('Set Role to Attendee')}
                                    </DropdownMenuItem>

                                    <DropdownMenuSeparator />

                                    <DropdownMenuItem
                                      disabled={attendee.status === 'GOING'}
                                      onClick={() => handleAction(attendee, 'going')}
                                    >
                                      <CheckIcon />
                                      {t('Going')}
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                      disabled={attendee.status === 'MAYBE'}
                                      onClick={() => handleAction(attendee, 'maybe')}
                                    >
                                      <ShieldQuestionIcon />
                                      {t('Maybe')}
                                    </DropdownMenuItem>

                                    <DropdownMenuItem
                                      disabled={attendee.status === 'NOTGOING'}
                                      onClick={() => handleAction(attendee, 'notgoing')}
                                    >
                                      <XIcon />
                                      {t('Not Going')}
                                    </DropdownMenuItem>

                                    {eventData?.event?.waitlistEnabled && (
                                      <DropdownMenuItem
                                        disabled={attendee.status === 'WAITLISTED'}
                                        onClick={() => handleAction(attendee, 'waitlisted')}
                                      >
                                        <ClipboardListIcon />
                                        {t('Waitlist')}
                                      </DropdownMenuItem>
                                    )}

                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      disabled={attendee.profileId === eventData?.event?.ownerId}
                                      onClick={() => handleAction(attendee, 'remove')}
                                      variant="destructive"
                                    >
                                      <UserMinusIcon />
                                      {t('Remove from Event')}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>

                            {eventData?.event.canEdit && attendee?.notes && (
                              <div className="flex w-full flex-col items-start pt-1">
                                <Separator className="w-full" />
                                <div className="flex items-start justify-start gap-2 pt-1">
                                  <p className="whitespace-pre-line text-muted-foreground text-sm">{t('Notes:')}</p>
                                  <p className="whitespace-pre-line text-muted-foreground text-sm">{attendee?.notes}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
                <PaginationControl
                  count={pageData.count}
                  onPageChange={handlePageChange}
                  page={currentPage}
                  pageSize={pageData.perPage}
                />
              </div>
            )}

            {pageData.entries.length === 0 && (
              <div className="flex flex-col items-center py-32">
                <p className="text-lg text-muted-foreground">{t('No profiles found')}</p>
                {watchName && (
                  <p className="text-muted-foreground text-sm">{t('Try adjusting your search or filters.')}</p>
                )}
              </div>
            )}
          </div>
        </Form>
      </div>

      {/* Remove Attendee Modal */}
      <ConfirmDialog
        cancelText={t('No')}
        confirmText={t('Yes')}
        onConfirm={() => {
          if (attendeeToRemove) {
            removeEventAttendee({
              variables: {
                id: attendeeToRemove.id,
                profileId: attendeeToRemove.profile.id,
              },
            })
          }
        }}
        onOpenChange={(e) => setRemoveModal(e.open)}
        open={removeModal}
        text={t('Are you sure you want to remove {{name}} from the event?', { name: attendeeToRemove?.profile?.name })}
        title={t('Remove Attendee')}
        variant="delete"
      />

      {selectedAttendee && (
        <EventAttendeeDialog
          attendee={selectedAttendee}
          event={eventData?.event}
          onOpenChange={(e) => setPaymentDialogOpen(e.open)}
          open={paymentDialogOpen}
        />
      )}
    </div>
  )
}
